"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Pagination,
  Tooltip,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Avatar,
  Container,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Chip,
  InputAdornment,
  alpha,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import GavelIcon from "@mui/icons-material/Gavel";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { IonPage } from "@ionic/react";
import { requestID, baseUrl, config } from "../../../common/utils";
 
interface CaseItem {
  id: string;
  name: string;
  case_id: string;
  status?: "active" | "inactive";
  createdAt?: string;
}

const CasePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState({
    table: false,
    submit: false,
    delete: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [formValues, setFormValues] = useState({ id: "", name: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCases = async () => {
    try {
      setLoading((prev) => ({ ...prev, table: true }));
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}whitsleBlowings/getAllWhistleBlowingCases`,
        payload,
        config
      );
      console.log(res.data.data);
      // Add mock status and dates for demo purposes
      const casesWithStatus = res.data.data.map(
        (caseItem: CaseItem, index: number) => ({
          ...caseItem,
          status: index % 4 === 0 ? "inactive" : "active",
          createdAt: new Date(Date.now() - index * 86400000)
            .toISOString()
            .split("T")[0],
        })
      );
      setCases(casesWithStatus);
    } catch (error) {
      showSnackbar("Error fetching cases", "error");
    } finally {
      setLoading((prev) => ({ ...prev, table: false }));
    }
  };

  const handleOpenDialog = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCase(null);
    setFormValues({ id: "", name: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const payload = selectedCase
      ? {
          requestID: requestID(),
          case_id: selectedCase.case_id,
          name: formValues.name,
        }
      : { requestID: requestID(), name: formValues.name };

    try {
      setLoading((prev) => ({ ...prev, submit: true }));
      const endpoint = selectedCase
        ? `${baseUrl()}whitsleBlowings/editWhistleBlowingCase`
        : `${baseUrl()}whitsleBlowings/addWhistleBlowingCase`;
      const res = await axios.post(endpoint, payload, config);
      console.log(res);
      showSnackbar(`Case ${selectedCase ? "updated" : "added"} successfully!`);
      fetchCases();
      handleCloseDialog();
    } catch (err) {
      showSnackbar("Something went wrong", "error");
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  const handleEdit = (caseItem: CaseItem) => {
    setSelectedCase(caseItem);
    setFormValues({ id: caseItem.id, name: caseItem.name });
    setOpenDialog(true);
  };

  const handleDelete = async (caseId: string) => {
    if (!window.confirm("Are you sure you want to delete this case?")) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      const payload = { requestID: requestID(), case_id: caseId };
      await axios.post(
        `${baseUrl()}whitsleBlowings/deleteWhistleBlowingCase`,
        payload,
        config
      );
      showSnackbar("Case deleted successfully!");
      fetchCases();
    } catch (error) {
      showSnackbar("Failed to delete case", "error");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const filteredCases = cases.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCases = filteredCases.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredCases.length / rowsPerPage);

  return (
    <IonPage className="pushDown">
      <Box sx={{ minHeight: "100vh", bgcolor: "grey.50", overflow: "scroll" }}>
        {/* Modern Header */}
        <AppBar
          position="static"
          elevation={1}
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ minHeight: 80 }}>
            <Avatar
              sx={{
                mr: 2,
                bgcolor: "primary.main",
                width: 48,
                height: 48,
              }}
            >
              <GavelIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Case Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage whistleblowing case types and categories
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={2}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchCases}
                variant="outlined"
                size="large"
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
              <Button
                onClick={handleOpenDialog}
                variant="contained"
                startIcon={<AddIcon />}
                size="large"
                sx={{ borderRadius: 2 }}
              >
                New Case Type
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {cases.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Case Types
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {cases.filter((c) => c.status === "active").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Cases
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    {cases.filter((c) => c.status === "inactive").length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Cases
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="h3" fontWeight="bold" color="info.main">
                    {filteredCases.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Filtered Results
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Main Content Card */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Search case types..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(1);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "background.default",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ textAlign: { md: "right" } }}>
                    <Button
                      startIcon={<FilterListIcon />}
                      variant="outlined"
                      sx={{ borderRadius: 2, mr: 1 }}
                    >
                      Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}
                    >
                      <TableCell sx={{ py: 2, pl: 3, fontWeight: 600 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ py: 2, fontWeight: 600 }}>
                        Case Title
                      </TableCell>
                      <TableCell sx={{ py: 2, fontWeight: 600 }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ py: 2, fontWeight: 600 }}>
                        Created
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 2,
                          pr: 3,
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading.table ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                          <Typography variant="body1" sx={{ mt: 2 }}>
                            Loading cases...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : paginatedCases.length > 0 ? (
                      paginatedCases.map((caseItem, index) => (
                        <TableRow
                          key={caseItem.case_id}
                          hover
                          sx={{
                            "&:last-child td": { borderBottom: 0 },
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.02),
                            },
                          }}
                        >
                          <TableCell sx={{ pl: 3 }}>
                            {(page - 1) * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight={500}>
                              {caseItem.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {caseItem.case_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                caseItem.status === "active"
                                  ? "Active"
                                  : "Inactive"
                              }
                              color={
                                caseItem.status === "active"
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{caseItem.createdAt || "N/A"}</TableCell>
                          <TableCell sx={{ pr: 3, textAlign: "right" }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEdit(caseItem)}
                                  size="medium"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(caseItem.case_id)}
                                  disabled={loading.delete}
                                  size="medium"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  {loading.delete ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Box sx={{ textAlign: "center" }}>
                            <SearchIcon
                              sx={{
                                fontSize: 48,
                                color: "text.secondary",
                                mb: 1,
                              }}
                            />
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              {searchTerm
                                ? "No matching cases found"
                                : "No case types available"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm
                                ? "Try adjusting your search terms or filters"
                                : "Get started by adding your first case type"}
                            </Typography>
                            {!searchTerm && (
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenDialog}
                                sx={{ mt: 2, borderRadius: 2 }}
                              >
                                Add New Case Type
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredCases.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {paginatedCases.length} of {filteredCases.length}{" "}
                    cases
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Add/Edit Case Dialog */}
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: { borderRadius: 3 },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "primary.main",
                color: "white",
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedCase ? "Edit Case Type" : "Add New Case Type"}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  sx={{ color: "white" }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Case Type Title"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                margin="normal"
                required
                variant="outlined"
                placeholder="e.g., Fraud, Harassment, Misconduct"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleCloseDialog}
                color="inherit"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={loading.submit || !formValues.name.trim()}
                startIcon={
                  loading.submit ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{ borderRadius: 2 }}
              >
                {selectedCase ? "Update Case" : "Create Case"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{
                width: "100%",
                borderRadius: 2,
                boxShadow: theme.shadows[3],
              }}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </IonPage>
  );
};

export default CasePage;
